/** @format */
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError, AxiosResponse } from "axios";
import { IPlainObject, IValidationResult } from "lobbie";
import { useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import HelpTooltip from "src/components/shared/tooltips/HelpTooltip";
import { useAxiosPublicPost } from "src/hooks/useAxios";
import * as yup from "yup";
import { API_PREFIX, VALID_PASSWORD_MESSAGE } from "src/constants";
import { handleError, HTTP_STATUS_CODES, notify, removeAllUserSessionData } from "src/utils";
import FormFieldError from "../forms/FormFieldError";
import LobbieSpinner from "../loading/LobbieSpinner";

const VALIDATION_SCHEMA = yup.object().shape({
    password: yup.string().required(),
    passwordConfirmation: yup.string().oneOf([yup.ref("password")], "Passwords must match."),
});

const DEFAULT_MESSAGE = "Please request a new password reset email.";

const ResetPassword: React.FC = () => {
    const poster = useAxiosPublicPost();
    const [isLoading, setLoading] = useState<boolean>(false);

    const {
        register,
        formState: { errors },
        handleSubmit,
    } = useForm<any>({
        resolver: yupResolver(VALIDATION_SCHEMA),
    });

    const routeQuery: string = window.location.search;
    const routeParams: URLSearchParams = new URLSearchParams(routeQuery);
    const token: string | null = routeParams.get("token");
    if (!token) return null;

    const onSubmit = async (data: IPlainObject) => {
        if (!token) {
            notify({
                level: "error",
                title: "Could not reset password.",
                message: DEFAULT_MESSAGE,
            });
            return;
        }
        setLoading(true);
        await poster(
            `${API_PREFIX}/forgotPassword/update`,
            {
                ...data,
                token,
            },
            (error: AxiosError) => {
                if (error.response?.status === HTTP_STATUS_CODES.FORBIDDEN) {
                    notify({
                        level: "error",
                        title: "Error resetting password.",
                        message:
                            "Password resets expire 15 minutes after they are requested. Please try requesting a new password reset.",
                        duration: 20000,
                    });
                    removeAllUserSessionData(true);
                    return;
                }
                throw error;
            },
        )
            .then((response: AxiosResponse | void) => {
                const result = response && (response.data as IValidationResult);
                if (!result) {
                    setLoading(false);
                    notify({
                        level: "error",
                        title: "Error resetting password.",
                        message: DEFAULT_MESSAGE,
                    });
                    return;
                }
                if (result.success) {
                    notify({
                        level: "success",
                        title: "Success",
                        message: "Password reset!",
                    });
                    setTimeout(() => {
                        window.location.pathname = "/";
                    }, 1500);
                    return;
                }
                setLoading(false);
                notify({
                    level: "error",
                    title: "Failed to reset password.",
                    message: result.message || DEFAULT_MESSAGE,
                });
            })
            .catch((error) => {
                setLoading(false);
                handleError(error);
            });
    };

    return (
        <div className="container mt-5" style={{ maxWidth: 500 }}>
            <div className="row">
                <div className="col">
                    <div className="mb-4 text-center">
                        <h3>Reset Password</h3>
                    </div>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Form.Group controlId="password">
                            <div className="row">
                                <div className="col-9">
                                    <HelpTooltip
                                        message={VALID_PASSWORD_MESSAGE}
                                        tooltipId="passwordTooltip"
                                        title="New password"
                                    />
                                </div>
                                <div className="col-3">
                                    <Link className="col" to="/" tabIndex={-1}>
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col">
                                    <Form.Control
                                        {...register("password")}
                                        name={"password"}
                                        type="password"
                                        placeholder="Password"
                                        autoComplete="on"
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        </Form.Group>
                        <FormFieldError message={errors.password?.message} />
                        <Form.Group controlId="passwordConfirmation" className="pt-2">
                            <Form.Label>New Password Confirmation</Form.Label>
                            <Form.Control
                                {...register("passwordConfirmation")}
                                name={"passwordConfirmation"}
                                type="password"
                                placeholder="Confirm Password"
                                autoComplete="on"
                                disabled={isLoading}
                            />
                        </Form.Group>
                        <FormFieldError message={errors.passwordConfirmation?.message} />
                        <Button
                            variant="primary"
                            className="mb-2 mt-3 w-100"
                            type="submit"
                            disabled={isLoading}
                        >
                            Reset Password
                        </Button>
                    </Form>
                </div>
                <div className="mt-2 text-center">
                    <img className="login-logo mt-2" src="powered_by_lobbie.svg" alt="logo" />
                </div>
                <div style={{ textAlign: "center" }}>
                    <LobbieSpinner isHidden={!isLoading} />
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
